import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';

const initialFormData = {
    itemMantenimientoId: 0,
    nombre: '',
    descripcion: '',
    activo: true,
};

const ConfiguracionMantenimiento = () => {
    const toast = useRef(null);
    const HOST_API = process.env.REACT_APP_URL;
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();

    const [formData, setFormData] = useState(initialFormData);
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);

    // Parámetros de la alerta "falla recurrente" de la ficha del activo (por barrio)
    const [alerta, setAlerta] = useState({ umbralCorrectivas: 3, ventanaDiasAlerta: 90 });
    const [guardandoAlerta, setGuardandoAlerta] = useState(false);

    const handleChange = (campo, valor) => setFormData(prev => ({ ...prev, [campo]: valor }));

    const fetchConfiguracionAlerta = useCallback(async () => {
        if (!barrio?.id) return;
        try {
            const { data } = await axios.get(`${HOST_API}api/configuracionMantenimiento/GetConfiguracion`, {
                params: { administracionId, barrioId: barrio.id },
            });
            setAlerta({ umbralCorrectivas: data.umbralCorrectivas, ventanaDiasAlerta: data.ventanaDiasAlerta });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar la configuración de la alerta', life: 3000 });
        }
    }, [barrio?.id, HOST_API, administracionId]);

    useEffect(() => { fetchConfiguracionAlerta(); }, [fetchConfiguracionAlerta]);

    const guardarAlerta = async () => {
        if (!alerta.umbralCorrectivas || alerta.umbralCorrectivas < 1 || !alerta.ventanaDiasAlerta || alerta.ventanaDiasAlerta < 1) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Ambos valores deben ser mayores a 0', life: 3000 });
            return;
        }
        setGuardandoAlerta(true);
        try {
            await axios.post(`${HOST_API}api/configuracionMantenimiento/SaveConfiguracion`, {
                administracionId,
                barrioId: barrio.id,
                umbralCorrectivas: alerta.umbralCorrectivas,
                ventanaDiasAlerta: alerta.ventanaDiasAlerta,
            });
            toast.current.show({ severity: 'success', summary: 'Guardado', detail: 'Configuración de la alerta guardada', life: 2500 });
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'No se pudo guardar la configuración', life: 3500 });
        } finally {
            setGuardandoAlerta(false);
        }
    };

    const fetchItems = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${HOST_API}api/itemsMantenimiento/GetItems?administracionId=${administracionId}&barrioId=${barrio.id}`
            );
            setItems(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los ítems', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, HOST_API, administracionId]);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.nombre.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'El nombre es obligatorio', life: 3000 });
            return;
        }
        try {
            await axios.post(`${HOST_API}api/itemsMantenimiento/SaveItem`, {
                ...formData,
                barrioId: barrio.id,
                administracionId,
            });
            setVisible(false);
            fetchItems();
            toast.current.show({
                severity: 'success',
                summary: formData.itemMantenimientoId ? 'Actualizado' : 'Creado',
                detail: `Ítem ${formData.itemMantenimientoId ? 'actualizado' : 'creado'} correctamente`,
                life: 2500,
            });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el ítem', life: 3000 });
        }
    };

    const handleEdit = (row) => {
        setFormData({
            itemMantenimientoId: row.itemMantenimientoId,
            nombre:               row.nombre || '',
            descripcion:          row.descripcion || '',
            activo:               row.activo,
        });
        setVisible(true);
    };

    const handleDelete = (id, nombre) => {
        confirmDialog({
            message: `¿Estás seguro de eliminar "${nombre}"?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/itemsMantenimiento/DeleteItem?itemMantenimientoId=${id}&administracionId=${administracionId}`);
                    fetchItems();
                    toast.current.show({ severity: 'success', summary: 'Eliminado', detail: 'Ítem eliminado', life: 2500 });
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar', life: 3000 });
                }
            },
        });
    };

    // Mismo badge que la columna Estado de TableBarrios
    const estadoTemplate = (row) => {
        const s = row.activo
            ? { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5', label: 'Activo' }
            : { bg: '#fef3f2', color: '#f04438', border: '#fecdca', label: 'No Activo' };
        return (
            <span style={{
                background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
            }}>
                {s.label}
            </span>
        );
    };

    const descripcionTemplate = (row) =>
        row.descripcion
            ? row.descripcion.length > 60 ? row.descripcion.substring(0, 60) + '…' : row.descripcion
            : <span style={{ color: '#98a2b3' }}>—</span>;

    return (
        <>
            <h4>Configuración Mantenimiento</h4>
            <br />

            <ConfirmDialog />
            <Toast ref={toast} />
            <LoadingOverlay visible={loading} />

            {barrio?.id && (
                <div style={cardStyle} className="mb-4">
                    <div style={{ fontWeight: 600, color: '#101828', marginBottom: '0.25rem' }}>Alerta de falla recurrente</div>
                    <p style={{ margin: '0 0 0.85rem', fontSize: '0.85rem', color: '#667085' }}>
                        La ficha del activo marca "falla recurrente" cuando tiene esta cantidad de correctivos (o más) dentro de la ventana de días indicada.
                    </p>
                    <div className="flex flex-wrap align-items-end gap-3">
                        <div>
                            <label style={labelStyle}>Cantidad de correctivos</label>
                            <InputNumber
                                value={alerta.umbralCorrectivas}
                                onValueChange={(e) => setAlerta(prev => ({ ...prev, umbralCorrectivas: e.value }))}
                                min={1}
                                max={999}
                                useGrouping={false}
                                inputClassName="p-inputtext-sm"
                                inputStyle={{ width: 120 }}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>En los últimos (días)</label>
                            <InputNumber
                                value={alerta.ventanaDiasAlerta}
                                onValueChange={(e) => setAlerta(prev => ({ ...prev, ventanaDiasAlerta: e.value }))}
                                min={1}
                                max={3650}
                                useGrouping={false}
                                inputClassName="p-inputtext-sm"
                                inputStyle={{ width: 120 }}
                            />
                        </div>
                        <Button label="Guardar" icon="pi pi-save" className="p-button-sm" loading={guardandoAlerta} onClick={guardarAlerta} />
                    </div>
                </div>
            )}

            <div className="flex align-items-center gap-3 mb-3">
                <Button
                    label="Nuevo Ítem"
                    icon="pi pi-plus"
                    onClick={() => { setFormData(initialFormData); setVisible(true); }}
                    disabled={!barrio?.id}
                />
                {!barrio?.id && (
                    <span style={{ color: '#f04438', fontSize: '0.85rem' }}>
                        Seleccioná un barrio para continuar
                    </span>
                )}
            </div>

            <Dialog
                header={formData.itemMantenimientoId ? 'Editar Ítem' : 'Nuevo Ítem'}
                visible={visible}
                style={{ width: '480px' }}
                breakpoints={{ '960px': '100vw' }}
                modal
                onHide={() => setVisible(false)}
            >
                <form onSubmit={handleSubmit}>
                    <div className="flex flex-column gap-3 mt-2">

                        <div>
                            <label style={labelStyle}>Nombre <span style={{ color: '#f04438' }}>*</span></label>
                            <InputText
                                value={formData.nombre}
                                onChange={(e) => handleChange('nombre', e.target.value)}
                                className="p-inputtext-sm w-full"
                                placeholder="Ej: Ascensor, Bomba de agua, Portón eléctrico"
                                maxLength={100}
                            />
                        </div>

                        <div>
                            <label style={labelStyle}>Descripción <span style={optStyle}>(opcional)</span></label>
                            <InputTextarea
                                value={formData.descripcion}
                                onChange={(e) => handleChange('descripcion', e.target.value)}
                                rows={3}
                                autoResize
                                className="w-full"
                                placeholder="Ubicación, detalles del ítem, etc."
                                maxLength={500}
                            />
                        </div>

                        <div className="flex justify-content-end gap-2 mt-2">
                            <Button label="Cancelar" type="button" severity="secondary" text onClick={() => setVisible(false)} />
                            <Button label="Guardar" icon="pi pi-save" type="submit" />
                        </div>

                    </div>
                </form>
            </Dialog>

            <div className="grid">
                <div className="col-12">
                    <DataTable
                        value={items}
                        paginator
                        rows={10}
                        showGridlines
                        rowsPerPageOptions={[5, 10, 25]}
                        responsiveLayout="scroll"
                        emptyMessage="No hay ítems de mantenimiento registrados para este barrio"
                        className="p-datatable-gridlines bg-white"
                        sortField="nombre"
                        sortOrder={1}
                    >
                        <Column field="nombre" header="Nombre" sortable style={{ fontWeight: 500 }} />
                        <Column field="descripcion" header="Descripción" body={descripcionTemplate} />
                        <Column
                            header="Estado"
                            style={{ width: '8rem' }}
                            body={estadoTemplate}
                        />
                        <Column
                            header="Acciones"
                            style={{ width: '8rem' }}
                            body={(row) => (
                                <div className="flex gap-2 justify-content-center">
                                    <Button icon="pi pi-pencil" rounded text severity="warning" onClick={() => handleEdit(row)} tooltip="Editar" />
                                    <Button icon="pi pi-trash" rounded className="btn-delete" onClick={() => handleDelete(row.itemMantenimientoId, row.nombre)} tooltip="Eliminar" />
                                </div>
                            )}
                        />
                    </DataTable>
                </div>
            </div>
        </>
    );
};

const labelStyle = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };
const optStyle   = { color: '#98a2b3', fontWeight: 400 };
const cardStyle  = { background: '#fff', border: '1px solid #eaecf0', borderRadius: 12, padding: '1rem 1.25rem' };

export default ConfiguracionMantenimiento;
